import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

const COBITInstructionPage = () => {
  const navigate = useNavigate();
  const [stakeholderName, setStakeholderName] = useState('');
  const [stakeholderEmail, setStakeholderEmail] = useState('');
  const [stakeholderDepartment, setStakeholderDepartment] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem("access_token");

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    axios.get(`${process.env.REACT_APP_API_URL}users/user/profile/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        const { name, email, department } = res.data;
        setStakeholderName(name || '');
        setStakeholderEmail(email || '');
        setStakeholderDepartment(department || 'N/A');
      })
      .catch(() => {
        setError("Unable to load user profile. Please login again.");
      });
  }, [navigate, token]);

  const handleSubmitDashboard = (e) => {
    e.preventDefault();
    navigate('/cobit/dashboard');
  };

  const handleSubmitAssessment = (e) => {
    e.preventDefault();
    navigate('/cobit/assessment');
  };

  return (
    <div className="min-h-screen flex flex-col bg-orange-50 text-gray-800">
      <Helmet>
        <title>Instructions | COBIT 2019</title>
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-10">
        <div className="bg-white shadow-xl rounded-2xl p-8 border-t-8 border-[#FF4D00]">

          <h2 className="text-3xl font-bold text-[#FF4D00] mb-6">
            📋 COBIT 2019 Maturity Assessment – Instructions
          </h2>

          {/* Stakeholder Details */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold mb-2">Participating Stakeholder</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input readOnly value={stakeholderName} className="p-3 border rounded-lg bg-gray-100" />
              <input readOnly value={stakeholderEmail} className="p-3 border rounded-lg bg-gray-100" />
              <input readOnly value={stakeholderDepartment} className="p-3 border rounded-lg bg-gray-100" />
            </div>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </section>

          <hr className="my-8 border-orange-200" />

          {/* Introduction */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold">Introduction</h3>
            <p className="mt-2 text-gray-600">
              This assessment evaluates the maturity of <strong>Governance and Management of Enterprise I&T</strong>
              using the <span className="text-[#FF4D00] font-semibold">COBIT 2019 Framework</span>.
            </p>
            <p className="mt-2 text-gray-600">
              Each <strong>COBIT domain represents a separate assessment</strong> and must be completed and submitted independently.
            </p>
          </section>

          <hr className="my-8 border-orange-200" />

          {/* Domains */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold mb-4">COBIT 2019 Domains Covered</h3>

            {/* EDM */}
            <div className="mb-6">
              <h4 className="font-semibold text-[#FF4D00]">EDM — Evaluate, Direct and Monitor</h4>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>EDM01 — Ensured Governance Framework Setting and Maintenance</li>
                <li>EDM02 — Ensured Benefits Delivery</li>
                <li>EDM03 — Ensured Risk Optimization</li>
                <li>EDM04 — Ensured Resource Optimization</li>
                <li>EDM05 — Ensured Stakeholder Engagement</li>
              </ul>
            </div>

            {/* APO */}
            <div className="mb-6">
              <h4 className="font-semibold text-[#FF4D00]">APO — Align, Plan and Organize</h4>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>APO01 — Managed I&T Management Framework</li>
                <li>APO02 — Managed Strategy</li>
                <li>APO03 — Managed Enterprise Architecture</li>
                <li>APO04 — Managed Innovation</li>
                <li>APO05 — Managed Portfolio</li>
                <li>APO06 — Managed Budget and Costs</li>
                <li>APO07 — Managed Human Resources</li>
                <li>APO08 — Managed Relationships</li>
                <li>APO09 — Managed Service Agreements</li>
                <li>APO10 — Managed Vendors</li>
                <li>APO11 — Managed Quality</li>
                <li>APO12 — Managed Risk</li>
                <li>APO13 — Managed Security</li>
                <li>APO14 — Managed Data</li>
              </ul>
            </div>

            {/* BAI */}
            <div>
              <h4 className="font-semibold text-[#FF4D00]">BAI — Build, Acquire and Implement</h4>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>BAI01 — Managed Programs</li>
                <li>BAI02 — Managed Requirements Definition</li>
                <li>BAI03 — Managed Solutions Identification and Build</li>
                <li>BAI04 — Managed Availability and Capacity</li>
                <li>BAI05 — Managed Organizational Change</li>
                <li>BAI06 — Managed IT Changes</li>
                <li>BAI07 — Managed IT Change Acceptance and Transitioning</li>
                <li>BAI08 — Managed Knowledge</li>
                <li>BAI09 — Managed Assets</li>
                <li>BAI10 — Managed Configuration</li>
                <li>BAI11 — Managed Projects</li>
              </ul>
            </div>
          </section>

          <hr className="my-8 border-orange-200" />

          {/* How to Complete */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold">How to Complete the Assessment</h3>
            <ul className="list-disc list-inside mt-3 text-gray-700 space-y-2">
              <li>Answer each question with <strong>Yes</strong> or <strong>No</strong>.</li>
              <li>If <strong>Yes</strong>, provide a description and upload supporting evidence.</li>
              <li>Each domain must be completed before it can be submitted.</li>
              <li>Once submitted, a domain is considered finalized.</li>
            </ul>
          </section>

          {/* Actions */}
          <div className="flex justify-center gap-6 mt-10">
            <button
              onClick={handleSubmitDashboard}
              className="bg-[#FF4D00] hover:bg-orange-700 text-white px-6 py-3 rounded-full font-semibold shadow-md"
            >
              📊 View Dashboard
            </button>
            <button
              onClick={handleSubmitAssessment}
              className="bg-[#FF4D00] hover:bg-orange-700 text-white px-6 py-3 rounded-full font-semibold shadow-md"
            >
              📝 Start Assessment
            </button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default COBITInstructionPage;
